import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API } from '../api/client';
import { useApi } from '../api/useApi';
import { useRole } from '../phil/RoleContext';

const BADGE: Record<string, string> = {
  Active: 'badge badge-confirmed',
  Considering: 'badge badge-awaiting',
  Inactive: 'badge badge-declined',
};

const W: Record<string, string> = {
  H: 'w-h', M: 'w-m', L: 'w-l', TBD: 'w-tbd',
};

// Must match the backend's _VC_EIR_INDUSTRIES set (backend/app/routers/fis.py).
const VC_EIR_INDUSTRIES = ['Venture Capitalist', 'Executive In Residence'];

const STATUS_OPTIONS = ['Active', 'Considering', 'Inactive'];
const OUTREACH_OPTIONS = ['', 'Confirmed', 'Invite Sent', 'Awaiting RSVP', 'No Response', 'Declined'];

// Platform roles an admin can pick when onboarding an FI contact. `contact` is
// the FI-side role (see fi_participant); these are the app_user role the person
// will hold once provisioned.
const ONBOARD_ROLE_LABEL: Record<string, string> = { evaluator: 'Evaluator', admin: 'Administrator' };
const ONBOARD_ROLE_OPTIONS = [
  { value: 'evaluator', label: 'Evaluator (scores this FI’s applicants)' },
  { value: 'admin', label: 'Administrator (FIL)' },
];

interface Participant {
  id?: number;
  name: string;
  title?: string;
  email?: string;
  // Onboarding intent (read-only from the API; null = not flagged).
  onboardRole?: string | null;
  onboardRequestedAt?: string | null;
  // Set once the account was actually provisioned + invite emailed (cognito mode).
  onboardProvisionedAt?: string | null;
  // True once linked to the durable account row (alembic 0030). Informational; the
  // email lock and remove-confirm key off onboardProvisionedAt (any provisioned
  // contact has a Cognito account, whether or not user_id was backfilled yet).
  linked?: boolean;
}
interface LabYear { year: number; outreachStatus?: string | null; callDate?: string | null; onSelectionCommittee?: boolean | null; }

const EMPTY_PARTICIPANT: Participant = { name: '', title: '', email: '' };

// Editable working copy of the parts of an FI this page can change. VC/EIR FIs use
// `contacts` (labelled "Primary Contact"); standard FIs use `execSponsor` +
// `chaperones`. All three are kept in the draft so switching modes is lossless.
interface Draft {
  shortName: string;
  industry: string;
  relationshipStatus: string;
  execSponsor: Participant | null;
  chaperones: Participant[];
  contacts: Participant[];
  // Client Account Lead — single, nullable. Applies to every FI type.
  cal: Participant | null;
  labYears: LabYear[];
}

function draftFromFi(fi: any): Draft {
  return {
    shortName: fi.shortName || '',
    industry: fi.industry || '',
    relationshipStatus: fi.relationshipStatus || '',
    execSponsor: fi.execSponsor ? { ...fi.execSponsor } : null,
    chaperones: (fi.chaperones || []).map((p: Participant) => ({ ...p })),
    contacts: (fi.contacts || []).map((p: Participant) => ({ ...p })),
    cal: fi.cal ? { ...fi.cal } : null,
    labYears: (fi.labYears || []).map((y: LabYear) => ({ ...y })),
  };
}

export default function FIProfile() {
  const { fiId } = useParams<{ fiId: string }>();
  const navigate = useNavigate();
  const [reloadKey, setReloadKey] = useState(0);
  const { data: fi, loading, error } = useApi<any>(() => API.getFI(fiId!), [fiId, reloadKey]);

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  // null = follow the latest year with data; a number pins the priorities filter.
  const [prioYear, setPrioYear] = useState<number | null>(null);
  // Priorities table sorting. null = unsorted (source order).
  const [prioSort, setPrioSort] = useState<{ key: 'l2' | 'l3' | 'type' | 'weight'; dir: 'asc' | 'desc' } | null>(null);

  const { data: fiActions, loading: actionsLoading } = useApi<any[]>(() => API.fiActions(fiId!), [fiId]);

  const { role } = useRole();
  // Onboarding UI state — which contact's role picker is open, and the pending
  // pick. Onboarding only records intent (no account is created until Cognito).
  const [onboardPid, setOnboardPid] = useState<number | null>(null);
  const [onboardRole, setOnboardRole] = useState('evaluator');
  const [onboardBusy, setOnboardBusy] = useState(false);
  const [onboardError, setOnboardError] = useState('');
  // Result of the most recent "Resend invite", keyed by contact id so the
  // message renders next to the button that was clicked (success or failure).
  const [resendMsg, setResendMsg] = useState<{ pid: number; ok: boolean; text: string } | null>(null);
  const [resendingPid, setResendingPid] = useState<number | null>(null);

  // Delete-FI flow (admin only). A modal makes the admin type the FI's name before
  // the destructive Delete button enables — this is not reversible.
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const closeDelete = () => { setDeleteOpen(false); setDeleteConfirmText(''); setDeleteError(''); };

  const deleteFI = async () => {
    if (!fiId) return;
    setDeleting(true); setDeleteError('');
    try {
      await API.deleteFI(fiId);
      navigate('/feedback-priorities');
    } catch (e: any) {
      setDeleteError(e?.message || 'Failed to delete this FI. Please try again.');
      setDeleting(false);
    }
  };

  const openOnboard = (pid: number) => { setOnboardPid(pid); setOnboardRole('evaluator'); setOnboardError(''); };
  const cancelOnboard = () => { setOnboardPid(null); setOnboardError(''); };

  const confirmOnboard = async (pid: number) => {
    if (!fiId) return;
    setOnboardBusy(true); setOnboardError('');
    try {
      await API.onboardContact(fiId, pid, onboardRole);
      setOnboardPid(null);
      setReloadKey(k => k + 1);
    } catch (e: any) {
      setOnboardError(e?.message || 'Failed to record onboarding. Please try again.');
    } finally {
      setOnboardBusy(false);
    }
  };

  const revokeOnboard = async (pid: number) => {
    if (!fiId) return;
    setOnboardBusy(true); setOnboardError(''); setResendMsg(null);
    try {
      await API.revokeOnboardContact(fiId, pid);
      setReloadKey(k => k + 1);
    } catch (e: any) {
      const msg = e?.message || 'Failed to remove access. Please try again.';
      // Surface in both branches this handler serves: the picker uses onboardError,
      // the provisioned card uses the inline per-contact message.
      setOnboardError(msg);
      setResendMsg({ pid, ok: false, text: msg });
    } finally {
      setOnboardBusy(false);
    }
  };

  const resendInvite = async (pid: number) => {
    if (!fiId) return;
    setResendingPid(pid); setResendMsg(null);
    try {
      await API.resendContactInvite(fiId, pid);
      setResendMsg({ pid, ok: true, text: 'Invite re-sent — the temporary password email is on its way.' });
    } catch (e: any) {
      setResendMsg({ pid, ok: false, text: e?.message || 'Failed to resend the invite. Please try again.' });
    } finally {
      setResendingPid(null);
    }
  };

  // Change a provisioned contact's sign-in email in place (updates Cognito + the
  // account + the contact together). Deliberately a distinct action from editing the
  // (locked) email field in the contact form — it's an identity change.
  const changeContactEmail = async (pid: number, current: string) => {
    if (!fiId) return;
    const next = window.prompt(`New sign-in email for this contact (current: ${current}):`, current);
    if (next == null) return; // cancelled
    const email = next.trim();
    if (!email || email.toLowerCase() === (current || '').toLowerCase()) return;
    if (!window.confirm(
      `Change this contact’s sign-in email to ${email}? They’ll use the new address to sign in. ` +
      `If they haven’t activated their account yet, use “Resend invite” afterward to send the login to the new address.`,
    )) return;
    setResendingPid(pid); setResendMsg(null);
    try {
      await API.changeContactEmail(fiId, pid, email);
      setResendMsg({ pid, ok: true, text: `Sign-in email changed to ${email}.` });
      setReloadKey(k => k + 1);
    } catch (e: any) {
      setResendMsg({ pid, ok: false, text: e?.message || 'Failed to change the email. Please try again.' });
    } finally {
      setResendingPid(null);
    }
  };

  // Reset the draft whenever we leave edit mode or fresh data arrives.
  useEffect(() => {
    if (!editing) setDraft(null);
  }, [editing]);

  if (loading) return <div className="phil-loading">Loading…</div>;
  if (error || !fi) return <div className="phil-error">Unable to load FI profile. {error}</div>;

  const beginEdit = () => { setDraft(draftFromFi(fi)); setSaveError(''); setEditing(true); };
  const cancelEdit = () => { setEditing(false); setDraft(null); setSaveError(''); };

  const patchDraft = (patch: Partial<Draft>) => setDraft(d => (d ? { ...d, ...patch } : d));

  // VC / Executive-in-Residence FIs use a single "Primary Contact" list instead of
  // the Executive Sponsor + Chaperone structure. Follow the draft's industry while
  // editing so the sections switch responsively.
  const isVcEir = VC_EIR_INDUSTRIES.includes(editing && draft ? draft.industry : (fi.industry || ''));

  // Executive Sponsor — single, nullable.
  const setExecSponsor = (field: keyof Participant, value: string) =>
    setDraft(d => (d ? { ...d, execSponsor: { ...(d.execSponsor ?? EMPTY_PARTICIPANT), [field]: value } } : d));

  const clearExecSponsor = () => patchDraft({ execSponsor: null });

  // CAL (Client Account Lead) — single, nullable; shown for every FI type.
  const setCal = (field: keyof Participant, value: string) =>
    setDraft(d => (d ? { ...d, cal: { ...(d.cal ?? EMPTY_PARTICIPANT), [field]: value } } : d));

  const clearCal = () => patchDraft({ cal: null });

  // Chaperones / Primary Contacts — list-valued.
  type ListRole = 'chaperones' | 'contacts';
  const addListParticipant = (role: ListRole) =>
    setDraft(d => (d ? { ...d, [role]: [...d[role], { ...EMPTY_PARTICIPANT }] } : d));

  const setListParticipant = (role: ListRole, idx: number, field: keyof Participant, value: string) =>
    setDraft(d => (d ? { ...d, [role]: d[role].map((p, i) => (i === idx ? { ...p, [field]: value } : p)) } : d));

  const removeListParticipant = (role: ListRole, idx: number) =>
    setDraft(d => (d ? { ...d, [role]: d[role].filter((_, i) => i !== idx) } : d));

  const addLabYear = () =>
    setDraft(d => {
      if (!d) return d;
      const maxYear = d.labYears.reduce((m, y) => Math.max(m, y.year), new Date().getFullYear() - 1);
      return { ...d, labYears: [...d.labYears, { year: maxYear + 1, outreachStatus: '', callDate: '', onSelectionCommittee: false }] };
    });

  const patchLabYear = (idx: number, patch: Partial<LabYear>) =>
    setDraft(d => (d ? { ...d, labYears: d.labYears.map((y, i) => (i === idx ? { ...y, ...patch } : y)) } : d));

  const removeLabYear = (idx: number) =>
    setDraft(d => (d ? { ...d, labYears: d.labYears.filter((_, i) => i !== idx) } : d));

  const save = async () => {
    if (!draft || !fiId) return;
    // Normalize a single participant to the API shape, or null if it's blank.
    // `id` is carried so the server updates the existing row in place (preserving
    // its account link) instead of delete-and-re-add; omitted for new contacts.
    const one = (p: Participant | null) =>
      p && p.name.trim() ? { id: p.id, name: p.name.trim(), title: p.title || null, email: p.email || null } : null;
    // A list, dropping entries with a blank name.
    const many = (ps: Participant[]) =>
      ps.map(one).filter((p): p is NonNullable<typeof p> => p !== null);
    // Send only the roles relevant to this FI's type so the other role's rows
    // (untouched, thus omitted) are preserved server-side.
    const contactsPayload = {
      cal: one(draft.cal),
      ...(isVcEir
        ? { contacts: many(draft.contacts) }
        : { execSponsor: one(draft.execSponsor), chaperones: many(draft.chaperones) }),
    };

    // Confirm before a save removes a linked contact — deleting it offboards their
    // platform account. Compare the original linked contacts (for the roles this
    // save touches) against the ids still present in the payload.
    const keptIds = new Set<number>();
    Object.values(contactsPayload).forEach(v => {
      (Array.isArray(v) ? v : v ? [v] : []).forEach((it: any) => { if (it?.id != null) keptIds.add(it.id); });
    });
    const originalTouched: Participant[] = [
      fi.cal,
      ...(isVcEir ? (fi.contacts || []) : [fi.execSponsor, ...(fi.chaperones || [])]),
    ].filter(Boolean) as Participant[];
    const removedLinked = originalTouched.filter(p => p.onboardProvisionedAt && p.id != null && !keptIds.has(p.id));
    if (removedLinked.length) {
      const names = removedLinked.map(p => p.name).join(', ');
      const ok = window.confirm(
        `This will remove platform access for ${names} — their evaluator/admin account will be offboarded (reversible via re-onboarding). Continue?`,
      );
      if (!ok) return;
    }

    setSaving(true);
    setSaveError('');
    try {
      await API.updateFI(fiId, {
        shortName: draft.shortName,
        industry: draft.industry,
        relationshipStatus: draft.relationshipStatus || null,
      });
      await API.updateFIContacts(fiId, contactsPayload);
      await API.updateFILabYears(fiId, {
        labYears: draft.labYears
          .filter(y => Number.isFinite(y.year))
          .map(y => ({
            year: Number(y.year),
            outreachStatus: y.outreachStatus || null,
            callDate: y.callDate || null,
            onSelectionCommittee: y.onSelectionCommittee ?? null,
          })),
      });
      setEditing(false);
      setDraft(null);
      setReloadKey(k => k + 1);
    } catch (e: any) {
      setSaveError(e?.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const actions: any[] = fiActions || [];

  const labYears: LabYear[] = editing && draft ? draft.labYears : (fi.labYears || []);
  const inputStyle: React.CSSProperties = { padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, width: '100%' };

  // --- Contact card renderers ------------------------------------------------
  const editFields = (p: Participant | null | undefined, onChange: (field: keyof Participant, value: string) => void) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
      <input placeholder="Name" value={p?.name || ''} onChange={e => onChange('name', e.target.value)} style={inputStyle} />
      <input placeholder="Title" value={p?.title || ''} onChange={e => onChange('title', e.target.value)} style={inputStyle} />
      {/* A provisioned contact's email is their sign-in identity — it can't be repointed
          in place. Lock it; changing who this is means Remove access, then re-onboard. */}
      <input
        placeholder="Email"
        value={p?.email || ''}
        onChange={e => onChange('email', e.target.value)}
        style={{ ...inputStyle, ...(p?.onboardProvisionedAt ? { background: '#f3f4f6', color: '#6b7280' } : null) }}
        readOnly={!!p?.onboardProvisionedAt}
        title={p?.onboardProvisionedAt ? 'This contact has a platform account — email is their sign-in identity. Use “Change email” on the contact to update it.' : undefined}
      />
      {p?.onboardProvisionedAt && (
        <div style={{ fontSize: 11, color: '#6b7280' }}>
          Email locked — has a platform account. Use “Change email” on the contact to update it.
        </div>
      )}
    </div>
  );

  const readView = (p: Participant) => (
    <>
      <div className="part-name">{p.name}</div>
      <div className="part-title">{p.title}</div>
      {p.email && <div className="part-email">{p.email}</div>}
    </>
  );

  // Admin-only "Onboard to platform" affordance shown on eligible contact cards
  // in read mode. Records intent (role picker) or shows the current flag + revoke.
  const onboardControl = (p: Participant) => {
    if (role !== 'admin' || editing || p.id == null) return null;
    const pid = p.id;
    const hasEmail = !!(p.email && p.email.trim());

    // Provisioned: account created + invite emailed. Offer a resend (for an
    // expired temp password) rather than the picker.
    if (p.onboardProvisionedAt) {
      return (
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span className="badge badge-confirmed">Invited ✓ · {ONBOARD_ROLE_LABEL[p.onboardRole || ''] || p.onboardRole}</span>
            <button className="btn btn-outline btn-sm" style={{ padding: '2px 8px', fontSize: 11 }} disabled={resendingPid === pid} onClick={() => resendInvite(pid)}>{resendingPid === pid ? 'Resending…' : 'Resend invite'}</button>
            <button
              className="btn btn-outline btn-sm"
              style={{ padding: '2px 8px', fontSize: 11 }}
              disabled={resendingPid === pid}
              title="Change this person’s sign-in email (updates their account)."
              onClick={() => changeContactEmail(pid, p.email || '')}
            >
              Change email
            </button>
            <button
              className="btn btn-outline btn-sm"
              style={{ padding: '2px 8px', fontSize: 11 }}
              disabled={onboardBusy}
              title="Revoke this person’s platform access (reversible — you can re-onboard them later)."
              onClick={() => {
                if (window.confirm(`Remove ${p.name || 'this contact'}’s platform access? They’ll lose their evaluator/admin role. You can re-onboard them later.`)) revokeOnboard(pid);
              }}
            >
              Remove access
            </button>
          </div>
          {resendMsg?.pid === pid && (
            <div style={{ fontSize: 12, color: resendMsg.ok ? '#1e7e45' : '#c0392b' }}>{resendMsg.text}</div>
          )}
        </div>
      );
    }
    // Intent recorded but not provisioned (e.g. mock mode) — allow revoke.
    if (p.onboardRole) {
      return (
        <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span className="badge badge-awaiting">Onboarding · {ONBOARD_ROLE_LABEL[p.onboardRole] || p.onboardRole}</span>
          <button className="btn btn-outline btn-sm" style={{ padding: '2px 8px', fontSize: 11 }} disabled={onboardBusy} onClick={() => revokeOnboard(pid)}>Revoke</button>
        </div>
      );
    }
    if (onboardPid === pid) {
      return (
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <select value={onboardRole} onChange={e => setOnboardRole(e.target.value)} style={{ ...inputStyle, width: 'auto' }}>
            {ONBOARD_ROLE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn btn-primary btn-sm" style={{ padding: '3px 10px', fontSize: 11 }} disabled={onboardBusy} onClick={() => confirmOnboard(pid)}>{onboardBusy ? 'Saving…' : 'Confirm'}</button>
            <button className="btn btn-outline btn-sm" style={{ padding: '3px 10px', fontSize: 11 }} disabled={onboardBusy} onClick={cancelOnboard}>Cancel</button>
          </div>
          {onboardError && <div className="phil-error" style={{ fontSize: 12 }}>{onboardError}</div>}
        </div>
      );
    }
    return (
      <button
        className="btn btn-outline btn-sm"
        style={{ marginTop: 8, padding: '3px 10px', fontSize: 11 }}
        disabled={!hasEmail}
        title={hasEmail ? 'Record intent to onboard this contact to the platform' : 'Add an email address for this contact first'}
        onClick={() => openOnboard(pid)}
      >
        Onboard to platform
      </button>
    );
  };

  // A list-valued role (Chaperone or Primary Contact): one card per entry, plus
  // add/remove controls in edit mode.
  // `listRole` is the FI-side role key. `onboardable(idx)` decides which cards get
  // the admin onboard affordance (per spec: all Primary Contacts; only the first
  // Chaperone on standard FIs).
  const renderContactList = (
    label: string,
    listRole: 'chaperones' | 'contacts',
    onboardable?: (idx: number) => boolean,
  ) => {
    const items: Participant[] = editing && draft ? draft[listRole] : (fi[listRole] || []);
    return (
      <>
        {items.map((p, i) => (
          <div className="participant-card" key={`${listRole}-${i}`}>
            <div className="part-role" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>{label}{items.length > 1 ? ` ${i + 1}` : ''}</span>
              {editing && draft && (
                <button className="btn btn-outline btn-sm" style={{ padding: '2px 8px', fontSize: 11 }} onClick={() => removeListParticipant(listRole, i)}>Remove</button>
              )}
            </div>
            {editing && draft ? (
              editFields(p, (field, value) => setListParticipant(listRole, i, field, value))
            ) : (
              <>
                {readView(p)}
                {onboardable && onboardable(i) && onboardControl(p)}
              </>
            )}
          </div>
        ))}
        {!editing && items.length === 0 && (
          <div className="participant-card">
            <div className="part-role">{label}</div>
            <div className="part-title" style={{ color: '#9ca3af' }}>Not assigned</div>
          </div>
        )}
        {editing && draft && (
          <button className="btn btn-outline btn-sm" style={{ gridColumn: '1 / -1', justifySelf: 'start' }} onClick={() => addListParticipant(listRole)}>
            + Add {label}
          </button>
        )}
      </>
    );
  };

  // A single-valued role (Executive Sponsor or CAL): one card, clearable in edit
  // mode. `role` is the draft/fi key; `onSet`/`onClear` drive the edit controls.
  const renderSingle = (
    label: string,
    role: 'execSponsor' | 'cal',
    onSet: (field: keyof Participant, value: string) => void,
    onClear: () => void,
  ) => {
    const p: Participant | null = editing && draft ? draft[role] : fi[role];
    return (
      <div className="participant-card">
        <div className="part-role" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{label}</span>
          {editing && draft && p && (
            <button className="btn btn-outline btn-sm" style={{ padding: '2px 8px', fontSize: 11 }} onClick={onClear}>Clear</button>
          )}
        </div>
        {editing && draft
          ? editFields(p, onSet)
          : p ? readView(p) : <div className="part-title" style={{ color: '#9ca3af' }}>Not assigned</div>}
      </div>
    );
  };

  // Priorities span multiple years; offer a year filter derived from lab years
  // (not just years with existing priorities, to avoid a chicken-and-egg problem).
  const allPriorities: any[] = fi.priorities || [];
  const labYearNums: number[] = (fi.labYears ?? []).map((y: any) => y.year).sort((a: number, b: number) => b - a);
  const priorityYears: number[] = labYearNums.length > 0 ? labYearNums : Array.from(new Set(allPriorities.map(p => p.year))).sort((a, b) => b - a);
  const effYear: number | null = prioYear ?? priorityYears[0] ?? null;
  const filteredPriorities = effYear == null ? allPriorities : allPriorities.filter(p => p.year === effYear);
  // Weight sorts by severity (H > M > L > TBD/blank), everything else alphabetically.
  const WEIGHT_RANK: Record<string, number> = { H: 0, M: 1, L: 2, TBD: 3 };
  const shownPriorities = prioSort
    ? [...filteredPriorities].sort((a, b) => {
        const dir = prioSort.dir === 'asc' ? 1 : -1;
        if (prioSort.key === 'weight') {
          const ra = WEIGHT_RANK[a.weight] ?? 99;
          const rb = WEIGHT_RANK[b.weight] ?? 99;
          return (ra - rb) * dir;
        }
        const va = String(a[prioSort.key] ?? '');
        const vb = String(b[prioSort.key] ?? '');
        return va.localeCompare(vb, undefined, { sensitivity: 'base' }) * dir;
      })
    : filteredPriorities;

  const togglePrioSort = (key: 'l2' | 'l3' | 'type' | 'weight') =>
    setPrioSort(prev =>
      prev && prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'asc' }
    );
  const prioArrow = (key: string) => (prioSort?.key === key ? (prioSort.dir === 'asc' ? ' ▲' : ' ▼') : '');

  return (
    <>
      <div className="breadcrumb">
        <a onClick={() => navigate('/feedback-priorities')}>Feedback &amp; Priorities</a>
        <span className="bc-sep">›</span>
        <span>{fi.name}</span>
      </div>

      <div className="page-header">

        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <h1>{fi.name}</h1>
          {editing && draft ? (
            <select
              value={draft.relationshipStatus}
              onChange={e => patchDraft({ relationshipStatus: e.target.value })}
              style={{ ...inputStyle, width: 'auto' }}
            >
              <option value="">— No status —</option>
              {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          ) : (
            fi.relationshipStatus && <span className={BADGE[fi.relationshipStatus] || 'badge'}>{fi.relationshipStatus}</span>
          )}
          {fi.onSelectionCommittee && <span className="badge badge-invite">Selection Committee</span>}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            {editing ? (
              <>
                <button className="btn btn-outline btn-sm" onClick={cancelEdit} disabled={saving}>Cancel</button>
                <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </>
            ) : (
              <>
                <button className="btn btn-outline btn-sm" onClick={beginEdit}>Edit</button>
                {role === 'admin' && (
                  <button
                    className="btn btn-outline btn-sm"
                    style={{ color: '#c0392b', borderColor: '#e5b4ae' }}
                    onClick={() => setDeleteOpen(true)}
                    title="Permanently delete this FI"
                  >
                    Delete FI
                  </button>
                )}
              </>
            )}
          </div>
        </div>
        {editing && draft ? (
          <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
            <input placeholder="Industry" value={draft.industry} onChange={e => patchDraft({ industry: e.target.value })} style={{ ...inputStyle, maxWidth: 260 }} />
            <input placeholder="Short name" value={draft.shortName} onChange={e => patchDraft({ shortName: e.target.value })} style={{ ...inputStyle, maxWidth: 180 }} />
          </div>
        ) : (
          <p className="subtitle">{[fi.industry, fi.shortName].filter(Boolean).join(' · ')}</p>
        )}
        {saveError && <div className="phil-error" style={{ marginTop: 10 }}>{saveError}</div>}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr', gap: 28, marginBottom: 24 }}>
        <div className="kpi-card" style={{ textAlign: 'center', padding: '14px 8px' }}>
          <div className="kpi-val" style={{ fontSize: 26 }}>{labYears.length}</div>
          <div className="kpi-label" style={{ fontSize: 11 }}>Years in the Lab</div>
        </div>
        <div className="two-col">
          {isVcEir ? (
            renderContactList('Primary Contact', 'contacts', () => true)
          ) : (
            <>
              {renderSingle('Executive Sponsor', 'execSponsor', setExecSponsor, clearExecSponsor)}
              {renderContactList('Chaperone', 'chaperones', i => i === 0)}
            </>
          )}
          {/* CAL applies to every FI type and drives the pre-call email flow. */}
          {renderSingle('Client Account Lead (CAL)', 'cal', setCal, clearCal)}
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2>Lab Participation History &nbsp;<span style={{ fontSize: 12, fontWeight: 400, color: '#9ca3af' }}>({labYears.length})</span></h2>
          {editing && draft && (
            <button className="btn btn-outline btn-sm" onClick={addLabYear}>+ Add Year</button>
          )}
        </div>
        {labYears.length === 0 ? (
          <div className="card-body" style={{ color: '#9ca3af' }}>No lab participation recorded.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Year</th>
                <th>Participation</th>
                <th>Outreach Status</th>
                <th>Call Date</th>
                <th>Selection Committee</th>
                {editing && <th></th>}
              </tr>
            </thead>
            <tbody>
              {labYears
                .map((y, i) => ({ y, i }))
                .sort((a, b) => a.y.year - b.y.year)
                .map(({ y, i }) => (
                  <tr key={i}>
                    {editing && draft ? (
                      <>
                        <td><input type="number" value={y.year} onChange={e => patchLabYear(i, { year: Number(e.target.value) })} style={{ ...inputStyle, width: 90 }} /></td>
                        <td><span style={{ color: '#059669', fontWeight: 600 }}>✓ Participated</span></td>
                        <td>
                          <select value={y.outreachStatus || ''} onChange={e => patchLabYear(i, { outreachStatus: e.target.value })} style={{ ...inputStyle, width: 'auto' }}>
                            {OUTREACH_OPTIONS.map(o => <option key={o} value={o}>{o || '—'}</option>)}
                          </select>
                        </td>
                        <td><input value={y.callDate || ''} onChange={e => patchLabYear(i, { callDate: e.target.value })} style={inputStyle} placeholder="e.g. Sep. 12th - 2 pm" /></td>
                        <td style={{ textAlign: 'center' }}><input type="checkbox" checked={!!y.onSelectionCommittee} onChange={e => patchLabYear(i, { onSelectionCommittee: e.target.checked })} /></td>
                        <td><button className="btn btn-outline btn-sm" style={{ padding: '2px 8px', fontSize: 11 }} onClick={() => removeLabYear(i)}>Remove</button></td>
                      </>
                    ) : (
                      <>
                        <td className="font-semibold">{y.year}</td>
                        <td><span style={{ color: '#059669', fontWeight: 600 }}>✓ Participated</span></td>
                        <td style={{ color: '#4b5563' }}>{y.outreachStatus || '—'}</td>
                        <td style={{ color: '#4b5563' }}>{y.callDate || '—'}</td>
                        <td>{y.onSelectionCommittee ? 'Yes' : '—'}</td>
                      </>
                    )}
                  </tr>
                ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="card mb-4">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <h2>Priorities &nbsp;<span style={{ fontSize: 12, fontWeight: 400, color: '#9ca3af' }}>({shownPriorities.length})</span></h2>
            {priorityYears.length > 0 && (
              <select
                value={effYear ?? ''}
                onChange={e => setPrioYear(Number(e.target.value))}
                style={{ ...inputStyle, width: 'auto' }}
                title="Priority year"
              >
                {priorityYears.map(y => <option key={y} value={y}>FY{y % 100}</option>)}
              </select>
            )}
          </div>
          <button
            className="btn btn-outline btn-sm"
            onClick={() => navigate(`/calls/${fi.id}/review`)}
          >
            Edit priorities →
          </button>
        </div>
        {shownPriorities.length === 0 ? (
          <div className="card-body" style={{ color: '#9ca3af' }}>
            No priorities recorded{effYear ? ` for FY${effYear % 100}` : ''}.
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th onClick={() => togglePrioSort('l2')} style={{ cursor: 'pointer', userSelect: 'none' }}>Category{prioArrow('l2')}</th>
                <th onClick={() => togglePrioSort('l3')} style={{ cursor: 'pointer', userSelect: 'none' }}>Priority{prioArrow('l3')}</th>
                <th onClick={() => togglePrioSort('type')} style={{ cursor: 'pointer', userSelect: 'none' }}>Type{prioArrow('type')}</th>
                <th onClick={() => togglePrioSort('weight')} style={{ cursor: 'pointer', userSelect: 'none' }}>Weight{prioArrow('weight')}</th>
              </tr>
            </thead>
            <tbody>
              {shownPriorities.map((p: any) => (
                <tr key={p.id}>
                  <td className="font-semibold">{p.l2}</td>
                  <td title={p.definition || ''}>{p.l3}</td>
                  <td style={{ color: '#6b7280' }}>{p.type}</td>
                  <td><span className={W[p.weight] || 'w-tbd'}>{p.weight}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="card mb-4">
        <div className="card-header">
          <h2>Action Items &nbsp;<span style={{ fontSize: 12, fontWeight: 400, color: '#9ca3af' }}>({actions.length})</span></h2>
        </div>
        <div className="action-table">
          <div className="action-col-hd">
            <span>Date</span>
            <span>Action</span>
            <span>Detail</span>
            <span>Status</span>
            <span />
          </div>
          {actionsLoading ? (
            <div style={{ padding: '24px 0', textAlign: 'center', color: '#888' }}>Loading…</div>
          ) : actions.length === 0 ? (
            <div style={{ padding: '24px 0', textAlign: 'center', color: '#888' }}>
              No outstanding action items at this time.
            </div>
          ) : actions.map((item: any, i: number) => {
            const clickable = !!item.link;
            return (
              <div
                key={item.id ?? i}
                className="action-l1-row action-l1-no-expand"
                style={clickable ? { cursor: 'pointer' } : undefined}
                role={clickable ? 'button' : undefined}
                onClick={clickable ? () => navigate(item.link) : undefined}
              >
                <div className="action-date">{item.date ?? '—'}</div>
                <div>
                  <div className="action-l1-label">{item.label ?? item.action}</div>
                </div>
                <div className="action-detail">{item.detail ?? '—'}</div>
                <div><span className="badge badge-open">{item.status}</span></div>
                <div className="action-l2-arrow">{clickable ? '›' : ''}</div>
              </div>
            );
          })}
        </div>
      </div>

      {deleteOpen && (
        <div
          onClick={() => !deleting && closeDelete()}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 10, padding: 24, width: 460, maxWidth: '90vw',
              boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
            }}
          >
            <h2 style={{ margin: '0 0 8px', fontSize: 18, color: '#c0392b' }}>Delete this FI?</h2>
            <p style={{ fontSize: 13, color: '#4b5563', lineHeight: 1.5, marginTop: 0 }}>
              This permanently deletes <strong>{fi.name}</strong> along with its contacts, lab-year
              history and priorities. Any contact with a platform account will lose their access.
              This cannot be undone.
            </p>
            <p style={{ fontSize: 13, color: '#4b5563', marginBottom: 6 }}>
              Type <strong>{fi.name}</strong> to confirm:
            </p>
            <input
              autoFocus
              value={deleteConfirmText}
              onChange={e => setDeleteConfirmText(e.target.value)}
              placeholder={fi.name}
              style={inputStyle}
              disabled={deleting}
            />
            {deleteError && <div className="phil-error" style={{ fontSize: 12, marginTop: 8 }}>{deleteError}</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button className="btn btn-outline btn-sm" onClick={closeDelete} disabled={deleting}>Cancel</button>
              <button
                className="btn btn-sm"
                style={{
                  background: '#c0392b', color: '#fff', borderColor: '#c0392b',
                  opacity: deleteConfirmText.trim() === fi.name.trim() && !deleting ? 1 : 0.5,
                }}
                disabled={deleteConfirmText.trim() !== fi.name.trim() || deleting}
                onClick={deleteFI}
              >
                {deleting ? 'Deleting…' : 'Delete FI'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
